import { SelectChangeEvent } from "@mui/material";
import { useMuiController } from "@sinch/components";
import { useCustomIntervalType } from "@sinch/components/application/Rate/useIntervalType";
import { useRatelistUnit } from "@sinch/components/application/RateInterval/useRatelistUnits";
import { CustomIntervalType as CustomIntervalTypeEnum, UnitName, UnitType } from "@sinch/core/entities/serverEnums";
import { isNotNilOrEmpty } from "ramda-adjunct";
import { ReactNode, useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";

interface UnitOption {
  /** Composite key used by the select, e.g. `basic:overtime` or `custom:time`. */
  value: string;
  label: ReactNode;
  /** The pair of form values this option writes back to the unit. */
  fieldValue: { unitType: UnitType; customType: CustomIntervalTypeEnum | null };
}

/**
 * Wires a single intersection child unit to the form: exposes the unitType/customType
 * controllers, the merged basic+custom option list, the currently selected composite key,
 * and a change handler that resets nested custom settings to their schema defaults.
 */
export const useIntersectionUnitField = (unitsPath: string, index: number) => {
  const { setValue } = useFormContext();
  const settingsPath = `${unitsPath}.${index}.settings`;
  const { field: unitTypeField } = useMuiController({ name: `${unitsPath}.${index}.unitType`, required: true });
  const { field: customTypeField } = useMuiController({ name: `${unitsPath}.${index}.customType`, required: true });

  const { options: rateUnitOptions, getRateUnitUnitName, loading: rateUnitLoading } = useRatelistUnit();
  const { getOptionsList, basicTypes, getCustomIntervalSchema } = useCustomIntervalType();

  const customTypesOptions = useMemo(
    () => getOptionsList().filter(({ value }) => basicTypes.includes(value as CustomIntervalTypeEnum)),
    [basicTypes]
  );

  const options = useMemo<UnitOption[]>(
    () => [
      ...rateUnitOptions
        .filter(({ value }) => getRateUnitUnitName(value) === UnitName.Hour && value !== UnitType.Custom)
        .map(({ value, label }) => ({
          value: `basic:${value}`,
          fieldValue: { unitType: value as UnitType, customType: null },
          label,
        })),
      ...customTypesOptions.map(({ value, label }) => ({
        value: `custom:${value}`,
        fieldValue: { unitType: UnitType.Custom, customType: value as CustomIntervalTypeEnum },
        label,
      })),
    ],
    [customTypesOptions, getRateUnitUnitName, rateUnitOptions]
  );

  const currentValue = useMemo(
    () =>
      options.find(
        ({ fieldValue }) =>
          fieldValue.unitType === unitTypeField.value && fieldValue.customType === customTypeField.value
      )?.value,
    [options, unitTypeField.value, customTypeField.value]
  );

  const handleChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const fieldValue = options.find(({ value }) => value === event.target.value)?.fieldValue;
      if (!fieldValue) {
        return;
      }
      unitTypeField.onChange(fieldValue.unitType);
      customTypeField.onChange(fieldValue.customType);
      if (isNotNilOrEmpty(fieldValue.customType)) {
        const defaults = getCustomIntervalSchema(fieldValue.customType as CustomIntervalTypeEnum);
        setValue(settingsPath, defaults.getDefault());
      }
    },
    [options, unitTypeField, customTypeField, getCustomIntervalSchema, setValue, settingsPath]
  );

  /** Fallback custom type used when a freshly-added unit has no customType yet. */
  const defaultCustomType = customTypesOptions[0].value as CustomIntervalTypeEnum;

  return {
    unitTypeField,
    customTypeField,
    options,
    currentValue,
    handleChange,
    defaultCustomType,
    loading: rateUnitLoading,
  };
};
